import { Button } from "@/v2-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import { useTrans } from "@/utils/useTrans";
import { GE_MAX_SHIPPING_WEIGHT_KG } from "@/libs/global-e/shipping-limit";
import { useOverweightDialogStore } from "@/store/overweight-dialog-store";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import BulkOrderInquiryDialog from "@/v2-components/bulk-order-inquiry-dialog";

/**
 * 배송 상한 초과 안내 — **바로구매(PDP)** 전용. 카트는 인라인 고지가 대신한다.
 *
 * 문구는 카트 인라인과 **같은 사실**을 말한다(수량을 줄이면 해제 · 대량 주문은 문의). 두 표면이 다른
 * 이야기를 하면 사용자가 어느 쪽을 믿을지 모른다.
 */
export function OverweightShippingDialog() {
  const { t } = useTrans(["gnb"]);
  const open = useOverweightDialogStore((s) => s.open);
  const hide = useOverweightDialogStore((s) => s.hide);
  const router = useRouter();
  // ★이 컴포넌트는 `_app` 에 **항상 마운트**돼 있으므로 여기 둔 state 는 바깥 다이얼로그가
  // 닫혀도 살아 있다 — lazy mount 호출처에서 sibling 이 silent fail 하는 형태
  // (docs/pitfalls.md "Dialog 내부 useState")가 아니다.
  const [inquiryOpen, setInquiryOpen] = useState(false);

  // ★라우트가 바뀌면 **이 컴포넌트가 소유한 「열림」을 전부** 닫는다. `_app` 상주라
  // 뒤로가기·다른 페이지 이동 뒤에도 그대로 남는데, 맥락이 사라진 화면에 안내나 문의 폼만
  // 떠 있게 된다.
  //
  // ★리셋을 **함수 하나로 묶은 이유**: 열림 상태가 둘(`open` · `inquiryOpen`)인데 R5 이전에는
  // `hide` 만 걸려 있어 **문의 폼이 남았다**. 상태를 하나 더 추가하면 여기도 같이 늘어야
  // 한다는 관계를 코드에 남긴다 — 핸들러에 함수를 직접 넘기면 그 관계가 안 보인다.
  //
  // ⚠️ **입력 중이면 날아간다.** 그래도 닫는 쪽을 택했다 — 폼은 Radix 다이얼로그라 포커스를
  // 가두므로 라우트가 바뀌는 경로는 브라우저 뒤로가기 정도이고, 그건 「이 화면을 떠난다」는
  // 명시적 의사다. 반대로 안 닫으면 **무관한 페이지에 이메일 입력 폼이 떠 있는다.**
  // (문의 폼 자체는 제출 후 라우팅하지 않으므로 이 리셋이 제출 흐름을 끊지 않는다.)
  const closeAll = useCallback(() => {
    hide();
    setInquiryOpen(false);
  }, [hide]);

  useEffect(() => {
    router.events.on("routeChangeComplete", closeAll);
    return () => router.events.off("routeChangeComplete", closeAll);
  }, [router.events, closeAll]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) hide();
      }}
    >
      <DialogContent className="flex max-w-[460px] flex-col p-8">
        <DialogHeader>
          <DialogTitle>
            {t("Order too large for a single shipment")}
          </DialogTitle>
        </DialogHeader>

        <DialogDescription className="whitespace-pre-line">
          {t(
            "This order is too large to ship in a single shipment. Reduce the quantity, or contact us for a bulk order.",
            {
              defaultValue:
                "This order is too large to ship in a single shipment. Reduce the quantity, or contact us for a bulk order.",
            },
          )}
        </DialogDescription>

        <DialogFooter className="mt-2 w-full items-center justify-end gap-2">
          {/* ★막다른 길로 두지 않는다. 이 게이트에 닿는 카트는 사실상 대량 주문이고
              (36.1kg 은 Pro×4 + Air×4 급), 요율표에 값이 없을 뿐 **GE Admin 에서 운송사를
              바꾸면 배송은 가능**하다. "수량을 줄이라"만 말하면 가장 값어치 있는 문의를
              쫓아낸다. 같은 클래스의 차단인 `BulkOrderDialog`(수량 상한)가 이미 이 형태다. */}
          <Button
            size="sm"
            variant="blue-outline"
            shape="pill"
            className="px-7"
            onClick={() => {
              // 먼저 닫고 연다 — `BulkOrderDialog` 와 같은 순서(포커스 경합 방지).
              hide();
              setInquiryOpen(true);
            }}
          >
            {t("Request Order")}
          </Button>
          <Button
            size="sm"
            variant="blue-fill"
            shape="pill"
            className="px-7"
            onClick={hide}
          >
            {t("Confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
      {inquiryOpen && (
        <BulkOrderInquiryDialog
          open
          onOpenChange={setInquiryOpen}
          // CS 가 "왜 문의가 왔는지" 를 알아야 운송사 변경으로 처리할지 판단할 수 있다.
          desiredProduct={`Shipping weight limit exceeded (${GE_MAX_SHIPPING_WEIGHT_KG} kg or more)`}
        />
      )}
    </Dialog>
  );
}
